import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Calculator, Package, Euro, BarChart3 } from 'lucide-react'

import { useForm } from '../../hooks/useForm'
import { useToast } from '../Toast'
import Button from '../ui/Button'
import Input from '../ui/Input'
import Select from '../ui/Select'
import TextArea from '../ui/Textarea'

import produtosService, { 
  ProdutoFormData,
  formatarValor,
  calcularMargemLucro,
  calcularPrecoVenda,
  gerarSKU,
  validarSKU,
  validarCodigoBarras
} from '../../services/produtos'
import { Produto } from '../../types'

interface ProdutoFormProps {
  produto?: Produto
  onSuccess: () => void
  onCancel: () => void
}

const ProdutoForm: React.FC<ProdutoFormProps> = ({
  produto,
  onSuccess,
  onCancel
}) => {
  const { success, error } = useToast()
  
  // Estados locais
  const [calcularPrecoAutomatico, setCalcularPrecoAutomatico] = useState(false)
  const [calcularMargemAutomatico, setCalcularMargemAutomatico] = useState(false)
  const [skuGeradoAutomaticamente, setSkuGeradoAutomaticamente] = useState(false)

  // Query para obter categorias
  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => produtosService.getCategorias()
  })

  // Form
  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue
  } = useForm({
    initialValues: {
      nome: produto?.nome || '',
      descricao: produto?.descricao || '',
      marca: produto?.marca || '',
      modelo: produto?.modelo || '',
      categoriaId: produto?.categoria?.id || '',
      codigoBarras: produto?.codigoBarras || '',
      sku: produto?.sku || '',
      preco: produto?.preco || 0,
      precoCusto: produto?.precoCusto || 0,
      margemLucro: produto?.margemLucro || 0,
      estoqueMinimo: produto?.estoqueMinimo || 0,
      estoqueAtual: produto?.estoqueAtual || 0,
      localizacao: produto?.localizacao || '',
      observacoes: produto?.observacoes || '',
      tipo: produto?.tipo || 'PRODUTO',
      ativo: produto?.ativo ?? true
    },
    validationRules: {
      nome: {
        required: 'Nome é obrigatório',
        minLength: { value: 2, message: 'Nome deve ter pelo menos 2 caracteres' }
      },
      categoriaId: {
        required: 'Categoria é obrigatória',
        transform: (value) => value ? parseInt(value) : undefined
      },
      sku: {
        required: 'SKU é obrigatório',
        validate: (value: string) => {
          if (!validarSKU(value)) {
            return 'SKU deve ter entre 3 e 20 caracteres (apenas letras, números e hífens)'
          }
          return true
        }
      },
      preco: {
        required: 'Preço é obrigatório',
        min: { value: 0, message: 'Preço deve ser maior ou igual a zero' }
      },
      precoCusto: {
        required: 'Preço de custo é obrigatório',
        min: { value: 0, message: 'Preço de custo deve ser maior ou igual a zero' }
      },
      margemLucro: {
        min: { value: 0, message: 'Margem de lucro deve ser maior ou igual a zero' }
      },
      tipo: {
        required: 'Tipo é obrigatório'
      },
      estoqueMinimo: {
        min: { value: 0, message: 'Stock mínimo deve ser maior ou igual a zero' }
      },
      estoqueAtual: {
        min: { value: 0, message: 'Stock atual deve ser maior ou igual a zero' }
      },
      codigoBarras: {
        validate: (value: string) => {
          if (value && !validarCodigoBarras(value)) {
            return 'Código de barras inválido'
          }
          return true
        }
      }
    },
    onSubmit: async (formValues) => {
      try {
        const data: ProdutoFormData = {
          ...formValues,
          categoriaId: formValues.categoriaId ? parseInt(formValues.categoriaId.toString()) : undefined,
        }

        if (produto) {
          await produtosService.update(produto.id, data)
          success('Produto atualizado com sucesso')
        } else {
          await produtosService.create(data)
          success('Produto criado com sucesso')
        }
        
        onSuccess()
      } catch (err: any) {
        error('Erro ao salvar produto', err.message)
      }
    }
  })

  // Efeitos para cálculos automáticos
  useEffect(() => {
    if (calcularPrecoAutomatico && values.precoCusto && values.margemLucro) {
      const novoPreco = calcularPrecoVenda(Number(values.precoCusto), Number(values.margemLucro))
      setFieldValue('preco', Number(novoPreco.toFixed(2)))
    }
  }, [values.precoCusto, values.margemLucro, calcularPrecoAutomatico, setFieldValue])

  useEffect(() => {
    if (calcularMargemAutomatico && values.precoCusto && values.preco) {
      const novaMargem = calcularMargemLucro(Number(values.precoCusto), Number(values.preco))
      setFieldValue('margemLucro', Number(novaMargem.toFixed(2)))
    }
  }, [values.precoCusto, values.preco, calcularMargemAutomatico, setFieldValue])

  // Handlers
  const handleGerarSKU = () => {
    if (values.nome && values.categoriaId) {
      const categoriaNome = categorias?.find(cat => cat.id === values.categoriaId)?.nome || values.categoriaId.toString()
      const novoSKU = gerarSKU(values.nome, categoriaNome)
      setFieldValue('sku', novoSKU)
      setSkuGeradoAutomaticamente(true)
    }
  }

  const handleNomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFieldValue('nome', value)
    
    // Gerar SKU automaticamente se ainda não foi definido manualmente
    if (!produto && (!values.sku || skuGeradoAutomaticamente) && value && values.categoriaId) {
      const categoriaNome = categorias?.find(cat => cat.id === values.categoriaId)?.nome || ''
      const novoSKU = gerarSKU(value, categoriaNome)
      setFieldValue('sku', novoSKU)
      setSkuGeradoAutomaticamente(true)
    }
  }

  const handleCategoriaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const categoriaId = value ? parseInt(value) : undefined
    setFieldValue('categoriaId', categoriaId)
    
    // Gerar SKU automaticamente se ainda não foi definido manualmente
    if (!produto && (!values.sku || skuGeradoAutomaticamente) && values.nome && categoriaId) {
      const categoriaNome = categorias?.find(cat => cat.id === categoriaId)?.nome || ''
      const novoSKU = gerarSKU(values.nome, categoriaNome)
      setFieldValue('sku', novoSKU)
      setSkuGeradoAutomaticamente(true)
    }
  }

  const handleSKUChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('sku', e.target.value.toUpperCase())
    setSkuGeradoAutomaticamente(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna Esquerda - Informações Básicas */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Informações Básicas
            </h3>
            
            <div className="space-y-4">
              <Input
                label="Nome *"
                placeholder="Nome do produto"
                value={values.nome}
                onChange={handleNomeChange}
                error={errors.nome}
              />

              <TextArea
                label="Descrição"
                placeholder="Descrição detalhada do produto"
                rows={3}
                {...getFieldProps('descricao')}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Marca"
                  placeholder="Marca do produto"
                  {...getFieldProps('marca')}
                />

                <Input
                  label="Modelo"
                  placeholder="Modelo do produto"
                  {...getFieldProps('modelo')}
                />
              </div>

              <div className="space-y-2">
                <Select
                  label="Categoria *"
                  value={values.categoriaId}
                  onChange={(e) => setFieldValue('categoriaId', e.target.value)}
                  options={[
                    { value: '', label: 'Selecione uma categoria' },
                    ...(categorias?.map(categoria => ({
                      value: categoria.nome,
                      label: categoria.nome
                    })) || []),
                    { value: '__nova__', label: '+ Nova categoria' }
                  ]}
                  error={errors.categoriaId}
                />
                {values.categoriaId === '__nova__' && (
                  <Input
                    placeholder="Digite o nome da nova categoria"
                    onChange={(e) => setFieldValue('categoriaId', e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Códigos e Localização</h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">SKU *</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGerarSKU}
                    disabled={!values.nome || !values.categoriaId}
                  >
                    Gerar SKU
                  </Button>
                </div>
                <Input
                  placeholder="Código SKU"
                  value={values.sku}
                  onChange={handleSKUChange}
                  error={errors.sku}
                />
              </div>

              <Input
                label="Código de Barras"
                placeholder="Código de barras (EAN-8, EAN-13, UPC)"
                {...getFieldProps('codigoBarras')}
              />

              <Input
                label="Localização"
                placeholder="Localização no stock (ex: A1-B2)"
                {...getFieldProps('localizacao')}
              />

              <Select
                label="Tipo *"
                {...getFieldProps('tipo')}
                options={[
                  { value: 'PRODUTO', label: 'Produto (para venda)' },
                  { value: 'SERVICO', label: 'Peça para serviço' },
                  { value: 'PECA', label: 'Peça (uso geral)' }
                ]}
                error={errors.tipo}
              />
            </div>
          </div>
        </div>

        {/* Coluna Direita - Preços e Stock */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Preços e Margens
            </h3>
            
            <div className="space-y-4">
              <Input
                label="Preço de Custo *"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                {...getFieldProps('precoCusto')}
                rightIcon={<span className="text-gray-500">€</span>}
              />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Margem de Lucro (%)</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={calcularMargemAutomatico}
                      onChange={(e) => setCalcularMargemAutomatico(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-xs text-gray-600">Auto-calcular</span>
                  </label>
                </div>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...getFieldProps('margemLucro')}
                  rightIcon={<span className="text-gray-500">%</span>}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Preço de Venda *</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={calcularPrecoAutomatico}
                      onChange={(e) => setCalcularPrecoAutomatico(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-xs text-gray-600">Auto-calcular</span>
                  </label>
                </div>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...getFieldProps('preco')}
                  rightIcon={<span className="text-gray-500">€</span>}
                />
              </div>

              {values.precoCusto > 0 && values.preco > 0 && (
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <div className="text-sm text-blue-800">
                    <div>Margem calculada: {calcularMargemLucro(Number(values.precoCusto), Number(values.preco)).toFixed(2)}%</div>
                    <div>Lucro por unidade: {formatarValor(Number(values.preco) - Number(values.precoCusto))}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Controle de Stock
            </h3>

            <div className="space-y-4">
              <Input
                label="Stock Mínimo *"
                type="number"
                min="0"
                placeholder="0"
                {...getFieldProps('estoqueMinimo')}
              />

              <Input
                label={produto ? "Stock Atual (apenas leitura)" : "Stock Inicial *"}
                type="number"
                min="0"
                placeholder="0"
                {...getFieldProps('estoqueAtual')}
                disabled={!!produto}
                hint={produto ? "Use movimentações para alterar o stock" : undefined}
              />

              {produto && (
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <div className="text-sm text-yellow-800">
                    <strong>Atenção:</strong> Para alterar o stock de produtos existentes, 
                    use o sistema de movimentações para manter o histórico correto.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações</h3>
            
            <div className="space-y-4">
              <TextArea
                label="Observações"
                placeholder="Observações internas sobre o produto"
                rows={3}
                {...getFieldProps('observacoes')}
              />

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={values.ativo}
                  onChange={(e) => setFieldValue('ativo', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="ativo" className="ml-2 block text-sm text-gray-700">
                  Produto ativo
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          leftIcon={produto ? <Calculator className="h-4 w-4" /> : <Package className="h-4 w-4" />}
        >
          {produto ? 'Atualizar Produto' : 'Criar Produto'}
        </Button>
      </div>
    </form>
  )
}

export default ProdutoForm